import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import { Wifi, WifiOff, AlertTriangle, Signal, Ban, ChevronDown, ChevronRight, RefreshCw, Plus, Unplug } from 'lucide-react';
import { toast } from 'react-toastify';

import ParticleService from '../../ParticleService';

export default function DeviceManagerModal({ user, show, onHide }) {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(new Date());
    const [assignmentOpen, setAssignmentOpen] = useState(false);
    const [deviceId, setDeviceId] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [unclaimingDevice, setUnclaimingDevice] = useState(null);
    const [expandedDevices, setExpandedDevices] = useState(new Set());

    useEffect(() => {
        let intervalId;
        if (show && user) {
            setAssignmentOpen(false);
            setDeviceId('');
            setDeviceName('');
            setExpandedDevices(new Set());
            loadDeviceDetails();
            intervalId = setInterval(() => {
                loadDeviceDetails(true);
            }, 30000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [show, user]);

    const loadDeviceDetails = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // 1. Get list of devices
            const allDevices = await ParticleService.getProductDevices();
            // 2. Filter for user's devices
            const userDeviceDetails = allDevices.filter(d => 
                (user.devices || []).includes(d.id) || d.owner === user.username
            );

            // 3. FETCH REAL SIM STATUS
            const enhancedDevices = await Promise.all(userDeviceDetails.map(async (d) => {
                let simStatus = 'unknown';
                if (d.iccid) {
                    try {
                        const simData = await ParticleService.getSim(d.iccid);
                        if (simData && simData.status) {
                            simStatus = simData.status;
                        }
                    } catch (e) {
                        console.warn("Failed to fetch SIM for", d.name);
                    }
                }
                return { ...d, simStatus: simStatus };
            }));

            setDevices(enhancedDevices);
            setLastRefreshed(new Date());
        } catch (error) {
            console.error(error);
            if (!silent) toast.error("Failed to load device details");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const productId = user?.product?.toUpperCase() === 'WIFI'
        ? ParticleService.WIFIProductID
        : ParticleService.LTEProductID;
    const assignmentSupported = productId === ParticleService.LTEProductID;

    const handleAssignDevice = async event => {
        event.preventDefault();
        if (!deviceId.trim() || !deviceName.trim()) {
            toast.warn('Device ID and device name are required.');
            return;
        }
        setAssigning(true);
        try {
            let result = await ParticleService.assignProductDevice(
                productId, deviceId.trim(), user.username, deviceName.trim());
            if (result.requiresTransfer) {
                const confirmation = window.prompt(
                    `This device is assigned to ${result.currentOwner}. Type TRANSFER to remove that owner and assign it to ${user.username}. Access dates and subscriptions will not transfer.`
                );
                if (confirmation !== 'TRANSFER') return;
                result = await ParticleService.assignProductDevice(
                    productId, deviceId.trim(), user.username, deviceName.trim(), true);
            }
            if (result.error) {
                toast.error(result.detail || result.error);
                return;
            }
            if (result.warning) toast.warning(result.warning);
            toast.success(result.transferred
                ? `${deviceName.trim()} was transferred from ${result.previousOwner} to ${user.username}.`
                : `${deviceName.trim()} was assigned to ${user.username}.`);
            const synchronization = await ParticleService.synchronizeCustomerRegistry();
            if (synchronization.error) {
                toast.warning('The Particle assignment succeeded, but the local Sales registry could not be refreshed yet.');
            }
            setDeviceId('');
            setDeviceName('');
            setAssignmentOpen(false);
            document.dispatchEvent(new Event('dataRefresh'));
            await loadDeviceDetails();
        } catch (error) {
            console.error(error);
            toast.error('Device assignment failed.');
        } finally {
            setAssigning(false);
        }
    };

    const handleUnclaimDevice = async device => {
        const label = device.name || device.id;
        const confirmation = window.prompt(
            `Unclaim ${label} from ${user.username}?\n\n` +
            'Only do this when the physical device is no longer with this customer. ' +
            'If they still have the physical device, they can reclaim it at any time.\n\n' +
            'Type UNCLAIM to remove the current owner.'
        );
        if (confirmation !== 'UNCLAIM') return;

        setUnclaimingDevice(device.id);
        try {
            const result = await ParticleService.unclaimProductDevice(productId, device.id);
            if (result.error) {
                toast.error(result.detail || result.error);
                return;
            }
            toast.success(`${label} is now unclaimed in Particle.`);
            const synchronization = await ParticleService.synchronizeCustomerRegistry();
            if (synchronization.error) {
                toast.warning('Particle removed the owner, but the local customer registry could not be refreshed yet.');
            }
            document.dispatchEvent(new Event('dataRefresh'));
            await loadDeviceDetails();
        } catch (error) {
            console.error(error);
            toast.error('The device could not be unclaimed.');
        } finally {
            setUnclaimingDevice(null);
        }
    };

    const renderSimBadge = (status) => {
        const s = (status || '').toLowerCase();
        
        if (s === 'active') {
            return <Badge bg="primary" className="d-inline-flex align-items-center gap-1"><Signal size={12} /> Active</Badge>;
        } else if (s === 'deactivated' || s === 'inactive') {
            return <Badge bg="danger" className="d-inline-flex align-items-center gap-1"><Ban size={12} /> Deactivated</Badge>;
        } else {
            return <Badge bg="secondary" className="d-inline-flex align-items-center gap-1"><AlertTriangle size={12} /> {status || 'Unknown'}</Badge>;
        }
    };

    const toggleDevice = id => setExpandedDevices(current => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });

    const orderedDevices = [...devices].sort((left, right) => {
        const leftProblem = !left.connected || (left.simStatus || '').toLowerCase() !== 'active';
        const rightProblem = !right.connected || (right.simStatus || '').toLowerCase() !== 'active';
        if (leftProblem !== rightProblem) return leftProblem ? -1 : 1;
        return (left.name || left.id).localeCompare(right.name || right.id);
    });
    const onlineCount = devices.filter(device => device.connected).length;
    const activeSimCount = devices.filter(device => (device.simStatus || '').toLowerCase() === 'active').length;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <div className="d-flex align-items-center w-100 justify-content-between pe-4">
                    <Modal.Title>Manage Devices: {user?.username}</Modal.Title>
                    <div className="text-muted small d-flex align-items-center">
                        <span className="me-2">Auto-refreshing</span>
                        <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => loadDeviceDetails()}>
                            <RefreshCw size={14} className={loading ? "spin-anim" : ""} />
                        </Button>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex justify-content-end mb-3">
                    <Button
                        variant="primary"
                        onClick={() => setAssignmentOpen(current => !current)}
                        disabled={!assignmentSupported}
                        title={assignmentSupported ? 'Assign a new device to this Particle customer' : 'The Wi-Fi product needs separate two-legged OAuth credentials'}
                    >
                        <Plus size={16} className="me-1" /> Assign new device
                    </Button>
                </div>
                {!assignmentSupported && (
                    <Alert variant="info">Assignments for Wi-Fi customers require a separate two-legged Particle OAuth client for the Wi-Fi product.</Alert>
                )}
                {assignmentOpen && assignmentSupported && (
                    <Form onSubmit={handleAssignDevice} className="border rounded bg-light p-3 mb-4">
                        <h6>Assign device to {user?.username}</h6>
                        <div className="row g-2 align-items-end">
                            <Form.Group className="col-md-5">
                                <Form.Label>Device ID</Form.Label>
                                <Form.Control value={deviceId} onChange={event => setDeviceId(event.target.value)} required />
                            </Form.Group>
                            <Form.Group className="col-md-5">
                                <Form.Label>Device name</Form.Label>
                                <Form.Control value={deviceName} onChange={event => setDeviceName(event.target.value)} required />
                            </Form.Group>
                            <div className="col-md-2 d-grid">
                                <Button type="submit" disabled={assigning}>{assigning ? 'Assigning…' : 'Assign'}</Button>
                            </div>
                        </div>
                        <Form.Text className="text-muted">The device must be online and unclaimed in Particle.</Form.Text>
                    </Form>
                )}

                {loading && devices.length === 0 ? (
                    <div className="text-center p-5">Loading device details...</div>
                ) : devices.length === 0 ? (
                    <div className="text-center text-muted p-4">No detailed device info found.</div>
                ) : (
                    <>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Badge bg="light" text="dark" className="border px-3 py-2">{devices.length} device{devices.length === 1 ? '' : 's'}</Badge>
                        <Badge bg={onlineCount === devices.length ? 'success' : 'warning'} className="px-3 py-2">{onlineCount} online</Badge>
                        <Badge bg={activeSimCount === devices.length ? 'primary' : 'warning'} className="px-3 py-2">{activeSimCount} SIM active</Badge>
                    </div>
                    <div className="d-grid gap-2">
                        {orderedDevices.map(dev => {
                            const expanded = expandedDevices.has(dev.id);
                            return (
                            <Card key={dev.id} className="shadow-sm">
                                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-3 py-3">
                                    <div>
                                        <div className="fw-semibold">{dev.name || "Unnamed Device"}</div>
                                        <div className="small text-muted font-monospace">{dev.id}</div>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                                        {dev.connected ? (
                                            <Badge bg="success" className="d-inline-flex align-items-center gap-1"><Wifi size={12} /> Online</Badge>
                                        ) : (
                                            <Badge bg="secondary" className="d-inline-flex align-items-center gap-1"><WifiOff size={12} /> Offline</Badge>
                                        )}
                                        {renderSimBadge(dev.simStatus)}
                                        <span className="small text-muted d-none d-md-inline">Last seen {dev.last_heard ? new Date(dev.last_heard).toLocaleString() : 'never'}</span>
                                        <Button size="sm" variant="outline-secondary" aria-expanded={expanded} onClick={() => toggleDevice(dev.id)}>
                                            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />} {expanded ? 'Hide details' : 'View details'}
                                        </Button>
                                    </div>
                                </Card.Header>
                                {expanded && (
                                <Card.Body>
                                    <div className="row g-4 align-items-start">
                                        <div className="col-md-6 col-xl-4">
                                            <div className="small text-uppercase text-muted fw-semibold mb-2">Cloud activity</div>
                                            <div className="small text-muted mb-1">Last contact</div>
                                            {dev.last_heard ? (
                                                <div>{new Date(dev.last_heard).toLocaleString()}</div>
                                            ) : (
                                                <span className="text-muted">Never</span>
                                            )}
                                        </div>
                                        <div className="col-md-6 col-xl-4">
                                            <div className="small text-uppercase text-muted fw-semibold mb-2">Cellular service</div>
                                            <div className="mb-2">{renderSimBadge(dev.simStatus)}</div>
                                            <div className="small text-muted mb-1">ICCID</div>
                                            <div className="small font-monospace text-break">{dev.iccid || "No SIM data"}</div>
                                        </div>
                                        <div className="col-xl-4">
                                            <div className="small text-uppercase text-muted fw-semibold mb-2">Particle ownership</div>
                                            <div className="small text-muted mb-3">Remove this device from the current customer.</div>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                disabled={unclaimingDevice === dev.id}
                                                onClick={() => handleUnclaimDevice(dev)}
                                            >
                                                <Unplug size={14} className="me-1" />
                                                {unclaimingDevice === dev.id ? 'Unclaiming…' : 'Unclaim device'}
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                                )}
                            </Card>
                        );})}
                    </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="w-100 d-flex justify-content-between align-items-center">
                    <small className="text-muted">Last updated: {lastRefreshed.toLocaleTimeString()}</small>
                    <Button variant="secondary" onClick={onHide}>Close</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
